import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [activeProjectId, setActiveProjectIdState] = useState(
    localStorage.getItem('activeProjectId') || null
  );
  // isProjectReady = true, sobald activeProjectId endgültig aufgelöst wurde
  // (Backend-Antwort verarbeitet oder kein Token vorhanden). Pages MÜSSEN darauf
  // warten, bevor sie projekt-abhängige Requests feuern, um Race Conditions
  // beim Initial-Login / Page-Refresh zu vermeiden.
  const [isProjectReady, setIsProjectReady] = useState(!localStorage.getItem('token'));

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      setIsProjectReady(true);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const userData = response.data;
      setUser(userData);

      // Backend ist Source of Truth für active_project_id.
      // Wenn Backend einen Wert liefert, gilt dieser IMMER (überschreibt stale localStorage).
      // Nur wenn Backend keinen Wert hat, behalten wir localStorage als Fallback.
      if (userData.active_project_id) {
        localStorage.setItem('activeProjectId', userData.active_project_id);
        setActiveProjectIdState(userData.active_project_id);
      }
    } catch (error) {
      console.error('Auth error:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('activeProjectId');
      setToken(null);
      setUser(null);
      setActiveProjectIdState(null);
    } finally {
      setLoading(false);
      setIsProjectReady(true);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const setActiveProjectId = useCallback(async (projectId) => {
    if (!projectId || !token) return;
    // Optimistisch: neuen Projekt-Kontext sofort setzen und alte Requests
    // in Pages via useEffect-Cleanup abbrechen lassen.
    setIsProjectReady(false);
    setActiveProjectIdState(projectId);
    localStorage.setItem('activeProjectId', projectId);

    try {
      await axios.post(`${API}/projects/${projectId}/set-active`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Failed to set active project:', err);
    } finally {
      setIsProjectReady(true);
    }
  }, [token]);

  const login = async (email, password) => {
    setIsProjectReady(false);
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);

    if (userData.active_project_id) {
      localStorage.setItem('activeProjectId', userData.active_project_id);
      setActiveProjectIdState(userData.active_project_id);
    } else {
      // Backend hat kein aktives Projekt → localStorage aufräumen,
      // damit keine alten Werte durchsickern.
      localStorage.removeItem('activeProjectId');
      setActiveProjectIdState(null);
    }
    setIsProjectReady(true);

    return userData;
  };

  const register = async (name, email, password, sessionId = null) => {
    setIsProjectReady(false);
    const endpoint = sessionId 
      ? `${API}/onboarding/complete-registration?session_id=${sessionId}`
      : `${API}/auth/register`;
    
    const response = await axios.post(endpoint, { name, email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);

    if (userData.active_project_id) {
      localStorage.setItem('activeProjectId', userData.active_project_id);
      setActiveProjectIdState(userData.active_project_id);
    } else {
      localStorage.removeItem('activeProjectId');
      setActiveProjectIdState(null);
    }
    setIsProjectReady(true);

    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('activeProjectId');
    setToken(null);
    setUser(null);
    setActiveProjectIdState(null);
    setIsProjectReady(true);
  };

  const getAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    getAuthHeader,
    activeProjectId,
    setActiveProjectId,
    isProjectReady,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
